/**
 * Remaining admin pages: Listings (services & events), Verifications, Bookings,
 * Transactions, Withdrawals, Disputes, Settings.
 *
 * All operate on localStorage stores (with Supabase mirroring via syncStore
 * when configured). Each page exports a default component.
 */
import { useMemo, useState, useEffect } from "react";
import {
    Trash2,
    Star,
    ShieldCheck,
    FileText,
    ExternalLink,
    CheckCircle2,
    AlertTriangle,
    Save,
    RotateCcw,
} from "lucide-react";
import { getUsers, upsertUser } from "@/lib/storage";
import { getAllServices, getAllEvents } from "@/lib/entitiesStore";
import {
    getPlatformSettings,
    updatePlatformSettings,
    refreshPlatformSettingsFromBackend,
    DEFAULT_SETTINGS,
    subscribeSettings,
} from "@/lib/platformSettings";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const readLS = (k, f) => {
    try {
        const v = JSON.parse(localStorage.getItem(k) || JSON.stringify(f));
        // If caller expects an array, never return a non-array value.
        if (Array.isArray(f) && !Array.isArray(v)) return [];
        return v;
    } catch { return f; }
};
const writeLS = (k, v) => localStorage.setItem(k, JSON.stringify(v));

const readAllBookings = () => readLS("neyra.bookings.all", []);
const writeAllBookings = (v) => writeLS("neyra.bookings.all", v);

/* ------------------------------------------------------------------ */
/* LISTINGS                                                            */
/* ------------------------------------------------------------------ */

export const AdminListings = () => {
    const [v, setV] = useState(0);
    const services = useMemo(() => getAllServices(), [v]);
    const events = useMemo(() => getAllEvents(), [v]);

    const patchService = (id, patch) => {
        const all = readLS("neyra.services", []);
        const next = all.map((s) => (s.id === id ? { ...s, ...patch } : s));
        writeLS("neyra.services", next);
        // entitiesStore reads from a combined source — also update its user-created bucket
        const mine = readLS("neyra.services.user", []);
        writeLS("neyra.services.user", mine.map((s) => (s.id === id ? { ...s, ...patch } : s)));
        setV((x) => x + 1);
    };
    const removeService = (id) => {
        writeLS("neyra.services", readLS("neyra.services", []).filter((s) => s.id !== id));
        writeLS("neyra.services.user", readLS("neyra.services.user", []).filter((s) => s.id !== id));
        setV((x) => x + 1);
    };
    const patchEvent = (id, patch) => {
        writeLS("neyra.events", readLS("neyra.events", []).map((e) => (e.id === id ? { ...e, ...patch } : e)));
        writeLS("neyra.events.user", readLS("neyra.events.user", []).map((e) => (e.id === id ? { ...e, ...patch } : e)));
        setV((x) => x + 1);
    };
    const removeEvent = (id) => {
        writeLS("neyra.events", readLS("neyra.events", []).filter((e) => e.id !== id));
        writeLS("neyra.events.user", readLS("neyra.events.user", []).filter((e) => e.id !== id));
        setV((x) => x + 1);
    };

    return (
        <div data-testid="admin-listings" className="space-y-6">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Services & Events</h2>
                <p className="text-[13px] text-muted-foreground">
                    Moderate listings — mark as featured or remove inappropriate content.
                </p>
            </div>

            <section data-testid="admin-listings-services">
                <h3 className="font-display text-[14px] font-semibold mb-2">Services ({services.length})</h3>
                <ListingTable
                    rows={services}
                    onFeature={(id, cur) => patchService(id, { featured: !cur })}
                    onDelete={removeService}
                    kind="service"
                />
            </section>

            <section data-testid="admin-listings-events">
                <h3 className="font-display text-[14px] font-semibold mb-2">Events ({events.length})</h3>
                <ListingTable
                    rows={events}
                    onFeature={(id, cur) => patchEvent(id, { featured: !cur })}
                    onDelete={removeEvent}
                    kind="event"
                />
            </section>
        </div>
    );
};

const ListingTable = ({ rows, onFeature, onDelete, kind }) => (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <table className="w-full text-[13px]">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/50">
                <tr>
                    <th className="px-3 py-2.5">Title</th>
                    <th className="px-3 py-2.5 hidden md:table-cell">Category</th>
                    <th className="px-3 py-2.5 hidden md:table-cell">Location</th>
                    <th className="px-3 py-2.5">Price</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-border">
                {rows.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No {kind}s yet.</td></tr>
                )}
                {rows.map((r) => (
                    <tr key={r.id} data-testid={`admin-listing-${kind}-${r.id}`}>
                        <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2 min-w-0">
                                {r.image && <img src={r.image} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0" />}
                                <div className="min-w-0">
                                    <div className="font-medium truncate inline-flex items-center gap-1.5">
                                        {r.name || r.title}
                                        {r.featured && <Star size={11} className="text-amber-500 fill-amber-500" />}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground truncate">{r.providerName || r.hostName || "—"}</div>
                                </div>
                            </div>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">{r.category}</td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">{r.location}</td>
                        <td className="px-3 py-2.5 font-medium">
                            {r.priceFrom ? `From ${fmt(r.priceFrom)}` : r.price ? fmt(r.price) : "—"}
                        </td>
                        <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1.5">
                                <button
                                    data-testid={`admin-listing-${kind}-${r.id}-feature`}
                                    onClick={() => onFeature(r.id, r.featured)}
                                    className="h-8 px-2.5 rounded-lg border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 text-[11.5px] font-medium inline-flex items-center gap-1"
                                >
                                    <Star size={12} />
                                    {r.featured ? "Unfeature" : "Feature"}
                                </button>
                                <button
                                    data-testid={`admin-listing-${kind}-${r.id}-delete`}
                                    onClick={() => { if (window.confirm("Remove this listing?")) onDelete(r.id); }}
                                    className="h-8 px-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15 text-[11.5px] font-medium inline-flex items-center gap-1"
                                >
                                    <Trash2 size={12} />
                                    Remove
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

/* ------------------------------------------------------------------ */
/* VERIFICATIONS                                                       */
/* ------------------------------------------------------------------ */

const DocLink = ({ href, label }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11.5px] text-primary border border-border rounded-full px-2 py-0.5 hover:bg-primary/10"
    >
        <FileText size={10} /> {label} <ExternalLink size={10} />
    </a>
);

const readVerifRequests = () => readLS("neyra.verifications", []);
const writeVerifRequests = (v) => writeLS("neyra.verifications", v);

export const AdminVerifications = () => {
    const [v, setV] = useState(0);
    const requests = useMemo(() => readVerifRequests(), [v]);
    const users = useMemo(() => getUsers(), [v]);
    const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

    const act = (id, status, rejectionReason = null) => {
        const all = readVerifRequests();
        const next = all.map((r) =>
            r.id === id
                ? { ...r, status, rejectionReason, reviewedAt: Date.now() }
                : r
        );
        writeVerifRequests(next);
        const req = next.find((r) => r.id === id);
        const user = users.find((u) => u.id === req?.userId);
        if (!user) {
            setV((x) => x + 1);
            return;
        }
        const patch = { ...user };
        if (status === "approved") {
            // Personal / business standard verification → just verify the user.
            // Business upgrade → flip the account to business AND verify.
            if (req.kind === "business_upgrade") {
                patch.accountType = "business";
                patch.upgradeStatus = "approved";
            }
            patch.verified = true;
            patch.verificationStatus = "verified";
            patch.verificationKind = req.kind === "business_upgrade" ? "business" : req.kind;
            patch.rejectionReason = null;
        } else if (status === "rejected") {
            patch.verified = false;
            patch.verificationStatus = "rejected";
            patch.rejectionReason = rejectionReason || "Documents did not meet our requirements.";
            if (req.kind === "business_upgrade") patch.upgradeStatus = "rejected";
        }
        upsertUser(patch);
        setV((x) => x + 1);
    };

    const reject = (id) => {
        const reason = (typeof window !== "undefined" && window.prompt
            ? window.prompt("Rejection reason (shown to the user):", "")
            : null) || null;
        act(id, "rejected", reason);
    };

    return (
        <div data-testid="admin-verifications" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Verification requests</h2>
                <p className="text-[13px] text-muted-foreground">
                    Approve or reject pending requests, view uploaded documents, and assign the verified badge.
                </p>
            </div>
            <div className="rounded-2xl border border-border bg-card">
                {requests.length === 0 ? (
                    <div className="p-8 text-center text-[13px] text-muted-foreground" data-testid="admin-verifications-empty">
                        No verification requests yet.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {requests.map((r) => {
                            const u = userMap[r.userId];
                            return (
                                <li key={r.id} data-testid={`admin-verif-row-${r.id}`} className="p-3 flex items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate">
                                            {u?.fullName || r.userId}
                                            <span className="ml-2 text-[10.5px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-1.5 py-0.5">
                                                {r.kind || "personal"}
                                            </span>
                                            {r.paymentStatus === "paid" && (
                                                <span className="ml-1.5 text-[10.5px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 rounded-full px-1.5 py-0.5">
                                                    Paid
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11.5px] text-muted-foreground">
                                            {r.kind === "business_upgrade" ? (r.businessName || "Business upgrade") : (r.idType || "General verification")} · submitted {new Date(r.createdAt || Date.now()).toLocaleDateString()}
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {r.idDocument && <DocLink href={r.idDocument} label="ID document" />}
                                            {r.selfie && <DocLink href={r.selfie} label="Selfie" />}
                                            {r.cacDocument && <DocLink href={r.cacDocument} label="CAC" />}
                                            {r.ownerIdDocument && <DocLink href={r.ownerIdDocument} label="Owner ID" />}
                                            {r.addressProofDocument && <DocLink href={r.addressProofDocument} label="Address proof" />}
                                            {r.documentUrl && <DocLink href={r.documentUrl} label="Document" />}
                                        </div>
                                        {r.rejectionReason && (
                                            <p className="mt-1 text-[11.5px] text-destructive">
                                                Rejected: {r.rejectionReason}
                                            </p>
                                        )}
                                    </div>
                                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground self-center">
                                        {r.status || "pending"}
                                    </span>
                                    <button
                                        data-testid={`admin-verif-${r.id}-approve`}
                                        onClick={() => act(r.id, "approved")}
                                        className="h-8 px-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11.5px] font-medium inline-flex items-center gap-1 self-center"
                                    >
                                        <CheckCircle2 size={12} /> Approve
                                    </button>
                                    <button
                                        data-testid={`admin-verif-${r.id}-reject`}
                                        onClick={() => reject(r.id)}
                                        className="h-8 px-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-[11.5px] font-medium self-center"
                                    >
                                        Reject
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* BOOKINGS                                                            */
/* ------------------------------------------------------------------ */

export const AdminBookings = () => {
    const [v, setV] = useState(0);
    const [status, setStatus] = useState("all");
    const all = useMemo(() => readAllBookings(), [v]);
    const rows = useMemo(() => (status === "all" ? all : all.filter((b) => b.status === status)), [all, status]);

    return (
        <div data-testid="admin-bookings" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Bookings</h2>
                <p className="text-[13px] text-muted-foreground">Track booking status and escrow across all users.</p>
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="admin-bookings-status-filter">
                {["all", "pending", "released", "disputed", "cancelled"].map((s) => (
                    <button
                        key={s}
                        data-testid={`admin-bookings-status-${s}`}
                        onClick={() => setStatus(s)}
                        className={[
                            "h-9 px-3.5 rounded-full text-[12.5px] font-medium border",
                            status === s ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border hover:bg-muted",
                        ].join(" ")}
                    >
                        {s}
                    </button>
                ))}
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/50">
                        <tr>
                            <th className="px-3 py-2.5">Service</th>
                            <th className="px-3 py-2.5 hidden md:table-cell">Buyer → Seller</th>
                            <th className="px-3 py-2.5 hidden md:table-cell">Date</th>
                            <th className="px-3 py-2.5">Amount</th>
                            <th className="px-3 py-2.5">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {rows.length === 0 && (
                            <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No bookings.</td></tr>
                        )}
                        {rows.map((b) => (
                            <tr key={b.id} data-testid={`admin-booking-row-${b.id}`}>
                                <td className="px-3 py-2.5 font-medium">{b.serviceName}</td>
                                <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">{b.buyerName} → {b.sellerName}</td>
                                <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">{b.date} {b.time}</td>
                                <td className="px-3 py-2.5 font-semibold">{fmt(b.amount)}</td>
                                <td className="px-3 py-2.5">
                                    <span className="px-2 py-0.5 rounded-full bg-muted text-[10.5px] uppercase tracking-wider">{b.status}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* TRANSACTIONS                                                        */
/* ------------------------------------------------------------------ */

const readAllTxns = () => {
    try {
        const users = getUsers();
        const list = [];
        for (const u of users) {
            const raw = localStorage.getItem(`neara.wallet.${u.id}`);
            if (!raw) continue;
            const w = JSON.parse(raw);
            for (const t of w.txns || []) list.push({ ...t, userId: u.id, userName: u.fullName, available: w.available, pending: w.pending });
        }
        return list.sort((a, b) => (b.at || 0) - (a.at || 0));
    } catch { return []; }
};

export const AdminTransactions = () => {
    const [v] = useState(0);
    const txns = useMemo(() => readAllTxns(), [v]);
    const users = useMemo(() => getUsers(), [v]);

    return (
        <div data-testid="admin-transactions" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Transactions & wallets</h2>
                <p className="text-[13px] text-muted-foreground">Every wallet movement across the platform.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <section className="rounded-2xl border border-border bg-card p-3" data-testid="admin-wallets-section">
                    <h3 className="font-display text-[14px] font-semibold mb-2">Wallet balances</h3>
                    {users.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground">No users.</p>
                    ) : (
                        <ul className="divide-y divide-border">
                            {users.map((u) => {
                                const raw = localStorage.getItem(`neara.wallet.${u.id}`);
                                const w = raw ? JSON.parse(raw) : { available: 0, pending: 0 };
                                return (
                                    <li key={u.id} className="py-2 flex items-center gap-3 text-[12.5px]" data-testid={`admin-wallet-row-${u.id}`}>
                                        <span className="flex-1 truncate">{u.fullName || u.username}</span>
                                        <span className="text-muted-foreground">Avail {fmt(w.available)}</span>
                                        <span className="text-muted-foreground">Pending {fmt(w.pending)}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                <section className="rounded-2xl border border-border bg-card p-3" data-testid="admin-transactions-list">
                    <h3 className="font-display text-[14px] font-semibold mb-2">Latest transactions</h3>
                    {txns.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground">No transactions yet.</p>
                    ) : (
                        <ul className="divide-y divide-border max-h-[520px] overflow-y-auto">
                            {txns.slice(0, 200).map((t, i) => (
                                <li key={t.id || i} className="py-2 flex items-center gap-3 text-[12.5px]">
                                    <span className="font-medium truncate flex-1">{t.title || t.kind}</span>
                                    <span className="text-muted-foreground truncate">{t.userName || t.userId}</span>
                                    <span className={t.amount >= 0 ? "text-emerald-600" : "text-destructive"}>
                                        {t.amount >= 0 ? "+" : ""}{fmt(t.amount)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* WITHDRAWALS                                                         */
/* ------------------------------------------------------------------ */

const readWithdrawals = () => readLS("neyra.withdrawals", []);
const writeWithdrawals = (v) => writeLS("neyra.withdrawals", v);

export const AdminWithdrawals = () => {
    const [v, setV] = useState(0);
    const users = useMemo(() => getUsers(), []);
    const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
    const rows = useMemo(() => readWithdrawals().sort((a, b) => (b.at || 0) - (a.at || 0)), [v]);

    const decide = (id, decision) => {
        const next = readWithdrawals().map((w) =>
            w.id === id ? { ...w, status: decision, decidedAt: Date.now() } : w
        );
        writeWithdrawals(next);
        setV((x) => x + 1);
    };

    return (
        <div data-testid="admin-withdrawals" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Withdrawals</h2>
                <p className="text-[13px] text-muted-foreground">Approve or reject payout requests.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card">
                {rows.length === 0 ? (
                    <div className="p-8 text-center text-[13px] text-muted-foreground">No withdrawal requests yet.</div>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((w) => {
                            const u = userMap[w.userId];
                            return (
                                <li key={w.id} data-testid={`admin-withdraw-row-${w.id}`} className="p-3 flex items-center gap-3 text-[13px]">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{u?.fullName || w.userId}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            {w.bankName || "Bank"} · {w.accountNumber || "—"}
                                        </div>
                                    </div>
                                    <span className="font-semibold">{fmt(w.amount)}</span>
                                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{w.status || "pending"}</span>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            data-testid={`admin-withdraw-${w.id}-approve`}
                                            onClick={() => decide(w.id, "approved")}
                                            className="h-8 px-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11.5px] font-medium"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            data-testid={`admin-withdraw-${w.id}-reject`}
                                            onClick={() => decide(w.id, "rejected")}
                                            className="h-8 px-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-[11.5px] font-medium"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* DISPUTES                                                            */
/* ------------------------------------------------------------------ */

export const AdminDisputes = () => {
    const [v, setV] = useState(0);
    const rows = useMemo(
        () => readAllBookings().filter((b) => b.status === "disputed" || b.dispute),
        [v]
    );
    const resolve = (id, outcome) => {
        // outcome: "refund_buyer" | "release_seller" | "mark_resolved"
        const all = readAllBookings();
        const idx = all.findIndex((b) => b.id === id);
        if (idx < 0) return;
        const b = all[idx];
        if (outcome === "release_seller") {
            all[idx] = { ...b, status: "released", updatedAt: Date.now(), releasedAt: Date.now(),
                dispute: b.dispute ? { ...b.dispute, status: "resolved" } : null };
        } else if (outcome === "refund_buyer") {
            all[idx] = { ...b, status: "cancelled", updatedAt: Date.now(),
                dispute: b.dispute ? { ...b.dispute, status: "resolved" } : null };
        } else {
            all[idx] = { ...b, dispute: b.dispute ? { ...b.dispute, status: "resolved" } : null };
        }
        writeAllBookings(all);
        setV((x) => x + 1);
    };
    return (
        <div data-testid="admin-disputes" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Disputes & reports</h2>
                <p className="text-[13px] text-muted-foreground">Review issues reported by users and resolve.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card">
                {rows.length === 0 ? (
                    <div className="p-8 text-center text-[13px] text-muted-foreground" data-testid="admin-disputes-empty">
                        No disputes right now.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((b) => (
                            <li key={b.id} data-testid={`admin-dispute-row-${b.id}`} className="p-3 space-y-2">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle size={14} className="text-destructive" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{b.serviceName}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            {b.buyerName} ↔ {b.sellerName} · {fmt(b.amount)}
                                        </div>
                                    </div>
                                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{b.dispute?.status || "open"}</span>
                                </div>
                                {b.dispute?.reason && (
                                    <div className="text-[12.5px]"><b>Reason:</b> {b.dispute.reason}</div>
                                )}
                                {b.dispute?.detail && (
                                    <div className="text-[12px] text-muted-foreground">{b.dispute.detail}</div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        data-testid={`admin-dispute-${b.id}-release`}
                                        onClick={() => resolve(b.id, "release_seller")}
                                        className="h-8 px-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11.5px] font-medium"
                                    >
                                        Release to seller
                                    </button>
                                    <button
                                        data-testid={`admin-dispute-${b.id}-refund`}
                                        onClick={() => resolve(b.id, "refund_buyer")}
                                        className="h-8 px-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-[11.5px] font-medium"
                                    >
                                        Refund buyer
                                    </button>
                                    <button
                                        data-testid={`admin-dispute-${b.id}-resolve`}
                                        onClick={() => resolve(b.id, "mark_resolved")}
                                        className="h-8 px-2.5 rounded-lg border border-border bg-secondary text-[11.5px] font-medium"
                                    >
                                        Mark resolved
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* SETTINGS                                                            */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* REPORTS                                                             */
/* ------------------------------------------------------------------ */

export const AdminReports = () => {
    const [v, setV] = useState(0);
    const rows = useMemo(() => readLS("neyra.reports", []), [v]);

    const update = (id, patch) => {
        const list = readLS("neyra.reports", []);
        const idx = list.findIndex((r) => r.id === id);
        if (idx < 0) return;
        list[idx] = { ...list[idx], ...patch, updatedAt: new Date().toISOString() };
        writeLS("neyra.reports", list);
        setV((x) => x + 1);
    };

    return (
        <div data-testid="admin-reports" className="space-y-4">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Reports</h2>
                <p className="text-[13px] text-muted-foreground">
                    User-submitted reports against profiles, services and events.
                </p>
            </div>
            <div className="rounded-2xl border border-border bg-card">
                {rows.length === 0 ? (
                    <div className="p-8 text-center text-[13px] text-muted-foreground" data-testid="admin-reports-empty">
                        No reports right now.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((r) => (
                            <li key={r.id} data-testid={`admin-report-row-${r.id}`} className="p-3 space-y-2">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle size={14} className={r.status === "resolved" ? "text-muted-foreground" : "text-destructive"} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">
                                            {r.kind} · {r.targetName || r.targetId}
                                        </div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            Reported by {r.reporterId || "anonymous"} · {r.reason}
                                        </div>
                                    </div>
                                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                                        {r.status || "open"}
                                    </span>
                                </div>
                                {r.details && (
                                    <div className="text-[12px] text-muted-foreground rounded-xl bg-secondary/60 p-2.5">
                                        {r.details}
                                    </div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        data-testid={`admin-report-${r.id}-resolve`}
                                        onClick={() => update(r.id, { status: "resolved" })}
                                        className="h-8 px-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[11.5px] font-medium"
                                    >
                                        Mark resolved
                                    </button>
                                    <button
                                        data-testid={`admin-report-${r.id}-dismiss`}
                                        onClick={() => update(r.id, { status: "dismissed" })}
                                        className="h-8 px-2.5 rounded-lg border border-border bg-secondary text-[11.5px] font-medium"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* SETTINGS                                                            */
/* ------------------------------------------------------------------ */

export const AdminSettings = () => {
    const [s, setS] = useState(getPlatformSettings());
    const [saved, setSaved] = useState(false);

    useEffect(() => subscribeSettings(() => setS(getPlatformSettings())), []);
    // Pull canonical settings from backend on mount so admin always sees the
    // server-side state of pricing/commission, not just a stale local cache.
    useEffect(() => { refreshPlatformSettingsFromBackend().then(setS); }, []);

    const patch = (key, value) => setS((prev) => ({ ...prev, [key]: value }));
    const save = async () => {
        await updatePlatformSettings(s);
        setSaved(true); setTimeout(() => setSaved(false), 1800);
    };
    const reset = () => setS({ ...DEFAULT_SETTINGS });

    const fields = [
        { k: "premiumPriceMonthlyNgn", label: "Premium — monthly (₦)" },
        { k: "premiumPriceYearlyNgn",  label: "Premium — yearly (₦)" },
        { k: "verificationFeeNgn",     label: "Verification fee (₦)" },
        { k: "boostPrice24hNgn",       label: "Boost · 24h (₦)" },
        { k: "boostPrice3dNgn",        label: "Boost · 3 days (₦)" },
        { k: "boostPrice7dNgn",        label: "Boost · 7 days (₦)" },
        { k: "referralRewardNgn",      label: "Referral reward (₦)" },
        { k: "commissionPercent",      label: "Commission (%)" },
    ];

    return (
        <div data-testid="admin-settings" className="space-y-4 max-w-2xl">
            <div>
                <h2 className="font-display text-[22px] font-semibold tracking-tight">Platform settings</h2>
                <p className="text-[13px] text-muted-foreground">Tune pricing and commission. Changes apply instantly — no redeploy needed.</p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-3">
                    {fields.map((f) => (
                        <label key={f.k} className="block" data-testid={`admin-setting-${f.k}`}>
                            <span className="block text-[11.5px] font-medium text-muted-foreground mb-1 ml-0.5">{f.label}</span>
                            <input
                                type="number"
                                value={s[f.k] ?? 0}
                                onChange={(e) => patch(f.k, Number(e.target.value))}
                                className="w-full h-11 px-3 rounded-xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[14px]"
                            />
                        </label>
                    ))}
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="admin-setting-maintenance">
                    <input
                        type="checkbox"
                        checked={!!s.maintenanceMode}
                        onChange={(e) => patch("maintenanceMode", e.target.checked)}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                    />
                    <span className="text-[13px] font-medium">Maintenance mode (admin-only access)</span>
                </label>
                <div className="flex items-center gap-2 pt-2">
                    <button
                        data-testid="admin-settings-save"
                        onClick={save}
                        className="h-10 px-4 rounded-xl text-primary-foreground inline-flex items-center gap-2 text-[13px] font-medium active:scale-[0.98]"
                        style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                    >
                        <Save size={14} /> Save changes
                    </button>
                    <button
                        data-testid="admin-settings-reset"
                        onClick={reset}
                        className="h-10 px-4 rounded-xl border border-border bg-secondary text-foreground inline-flex items-center gap-2 text-[13px] font-medium"
                    >
                        <RotateCcw size={14} /> Reset defaults
                    </button>
                    {saved && <span className="text-[12px] text-emerald-600" data-testid="admin-settings-saved">Saved</span>}
                </div>
            </div>
        </div>
    );
};
